<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref, watch } from 'vue'
import { useAnalytics } from '@/composables/analytics'
import {
  useEcommerce,
  useEcommerceItemDetailsPurchase,
  useEcommercePrice,
  useLimitedTimeOffers,
} from '@/composables/game/ecommerce'
import { T } from '@tolgee/vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThePremium from '@/components/ThePremium.vue'
import { useConfetti } from '@/composables/game/confetti'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { syncPlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import ShopBadge from '@/components/ShopBadge.vue'
import afterStarterPackValueImage from '@/assets/images/shop-badge-500.png'
import turboStarterPackValueImage from '@/assets/images/shop-badge-400.png'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { formatDuration } from '@/helpers/date'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
  afterStarterPack: EcommerceItem
  show: boolean
  coolDown: number
}>()

defineEmits<{
  (e: 'hide'): void
}>()

const { triggerEvent } = useAnalytics()
const { buyingItemId, purchase, buildItemAnalyticsMetadata } = useEcommerce()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { afterStarterPackDuration } = useLimitedTimeOffers()
const { getStarPrice } = useEcommercePrice()
const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
} = useEcommerceItemDetailsPurchase(props.afterStarterPack)

const starPriceItem = computed(() => getStarPrice(props.afterStarterPack))
const purchaseDone = ref(false)
const coinBenefit = computed(() => props.afterStarterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COIN))
const spinBenefit = computed(() => props.afterStarterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN))
const isPurchasing = ref(false)
const isLoading = computed(() => buyingItemId.value !== null || isPurchasing.value)
const starPurchaseLoading = computed(() => isLoading.value && isBuyingWithStars.value)
const tonPurchaseLoading = computed(() => isLoading.value && isBuyingWithTon.value)

const initialCooldown = ref(props.coolDown)

const formattedTime = computed(() => formatDuration(afterStarterPackDuration.value - props.coolDown))

async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isPurchasing.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      showConfetti()
      purchaseDone.value = true
      await syncPlayer()
      await updateFortuneWheelState()
    }
  } finally {
    isPurchasing.value = false
  }
}

watch(() => props.show, show => {
  if (!show) return
  const itemMetadata = buildItemAnalyticsMetadata(props.afterStarterPack)
  triggerEvent({
    event: 'view_item',
    metadata: {
      ecommerce: {
        currency: 'USD',
        value: itemMetadata.price,
        items: buildItemAnalyticsMetadata(props.afterStarterPack),
      },
    },
  })
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="max-[375px]:pb-6"
    @hide="$emit('hide')"
  >
    <div
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <div class="size-[114px] relative">
        <ShopBadge
          :value-image="initialCooldown ? afterStarterPackValueImage : turboStarterPackValueImage"
          outer-class="-left-1.5 -top-4 w-[70px] h-auto"
          valueable-image-class="top-3"
          value-image-class="top-[28px]"
          animate-value-image
        />
        <img
          class="w-full h-full object-cover"
          src="@/assets/images/shop2-glow.png"
        >
      </div>
      <div class="mt-3 flex flex-col items-center">
        <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          <span v-if="purchaseDone">
            <T
              key-name="starter_pack.title_done"
              default-value="Purchase successful!"
            />
          </span>
          <template v-else>
            <T
              v-if="coolDown"
              key-name="after_starter_pack.title"
              default-value="Time-limited Pack"
            />
            <T
              v-else
              key-name="after_starter_pack.title2"
              default-value="Turbo Pack"
            />
            = ${{ starPriceItem.usdAmount }}
          </template>
        </div>
        <div class="text-center text-white/60 text-base font-medium max-w-[328px]">
          <template v-if="purchaseDone">
            <T
              v-if="initialCooldown"
              key-name="after_starter_pack.title"
              default-value="Time-limited Pack"
            />
            <T
              v-else
              key-name="after_starter_pack.title2"
              default-value="Turbo Pack"
            />
          </template>
          <template v-else>
            <T
              v-if="coolDown"
              key-name="after_starter_pack.subtitle"
              default-value="Grab it before it's gone—supercharge your growth now!"
            />
            <T
              v-else
              key-name="after_starter_pack.subtitle2"
              default-value="Speed up your progress with the Turbo Pack!"
            />
          </template>
        </div>
      </div>
      <div class="max-[375px]:mt-8 mt-12 flex flex-col items-center gap-y-3 w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <CoinIcon class="w-5 h-auto min-w-[20px]" />
          <NumberFormatTooltip
            :number="coinBenefit.amount"
            :formatting-type="'semi'"
          >
            <span
              class="benefit-box-value"
            >{{
              formatNumberSemiTruncate(coinBenefit.amount)
            }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && spinBenefit"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel-without-border.png"
            class="size-8 min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit.amount}"
            />
          </span>
        </div>
      </div>
      <div
        v-if="!purchaseDone && coolDown"
        class="mt-6 flex gap-y-1 flex-col items-center"
      >
        <div class="text-white/60 text-base font-medium">
          <T
            key-name="after_starter_pack.offer_ends_in"
            default-value="Offer ends in"
          />
        </div>
        <div
          class="rounded-full flex items-center justify-center px-8 py-[14px] bg-dark-most-bottom-layer text-white text-[24px] leading-5 font-medium -tracking-[0.48px]"
        >
          {{ formattedTime }}
        </div>
      </div>
      <BaseButton
        v-if="purchaseDone"
        class="w-full"
        :class="purchaseDone || (!purchaseDone && !coolDown) ? 'max-[375px]:mt-8 mt-12' : 'mt-6'"
        @click="$emit('hide')"
      >
        Close
      </BaseButton>
      <div
        v-else
        class="flex flex-col w-full"
      >
        <BaseButton
          class="w-full"
          :class="purchaseDone || (!purchaseDone && !coolDown) ? 'max-[375px]:mt-8 mt-12' : 'mt-6'"
          :loading="starPurchaseLoading"
          @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <span
            v-if="!starPurchaseLoading"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <ThePremium :with-tooltip="false" />
            <span class="-ml-0.5">{{ starPriceItem.amount }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>
        <div class="relative w-full">
          <div class="absolute right-12 top-1 rotate-[20deg]">
            <TonDiscountBadge label="-10%" />
          </div>
          <BaseButton
            v-if="showTonPurchase"
            style-type="tertiary"
            :loading="tonPurchaseLoading"
            class="mt-3 w-full"
            @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
          >
            <span
              v-if="!tonPurchaseLoading"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover"
              >
              <span>{{ tonPrice?.amount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";
</style>
